import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, Users, ArrowUpRight } from "lucide-react";
import { API } from "@/constants";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import SmartImage from "@/components/SmartImage";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/recipes`).then((r) => setRecipes(r.data.recipes || [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <main className="bg-white text-kera-ink font-body min-h-screen" data-testid="recipes-page">
      <Seo title="Recipes" description="Kerala recipes built around KERA products — from sadhya classics to quick weeknight curries, with ingredients and step-by-step methods." path="/recipes" />
      <Navbar />
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-16 pb-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-kera-copper mb-4"
        >
          KERA Kitchen
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="font-display font-bold tracking-tight text-kera-ink text-4xl sm:text-5xl"
          data-testid="recipes-page-title"
        >
          Recipes from God's Own Country
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16 }}
          className="mt-4 max-w-xl text-base text-kera-muted leading-relaxed"
        >
          Real Kerala tables, built with KERA packs — from freezer to feast in minutes.
        </motion.p>

        {recipes.length > 0 ? (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="recipes-grid">
            {recipes.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link to={`/recipes/${r.id}`} className="group block" data-testid={`recipe-card-${slug(r.title)}`}>
                  <div className="rounded-2xl overflow-hidden border border-kera-line">
                    <SmartImage
                      src={r.image}
                      alt={r.title}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="w-full h-64 object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display font-semibold text-xl text-kera-ink group-hover:text-kera-copper transition-colors leading-snug">
                        {r.title}
                      </h2>
                      <p className="mt-1.5 text-sm text-kera-muted leading-relaxed">{r.description}</p>
                      <div className="mt-3 flex gap-4 font-mono text-[10px] uppercase tracking-[0.18em] text-kera-muted">
                        {r.prep_time && <span className="inline-flex items-center gap-1"><Clock size={11} /> {r.prep_time}</span>}
                        {r.servings && <span className="inline-flex items-center gap-1"><Users size={11} /> {r.servings}</span>}
                      </div>
                    </div>
                    <span className="mt-1 shrink-0 h-9 w-9 rounded-full border border-kera-gold/60 flex items-center justify-center text-kera-copper transition-all duration-300 group-hover:bg-kera-gold group-hover:text-black">
                      <ArrowUpRight size={15} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="mt-16 text-center py-16 border border-dashed border-kera-line rounded-2xl" data-testid="recipes-empty">
            <p className="font-display font-semibold text-xl text-kera-ink">Recipes coming soon</p>
            <p className="mt-2 text-sm text-kera-muted">{loaded ? "Our kitchen is writing them up." : "Loading recipes…"}</p>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
